import { faDownload } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { DateTime } from 'luxon';
import { use, useEffect, useState } from 'react';
import { Button, Form, Spinner } from 'react-bootstrap';
import BTable from 'react-bootstrap/Table';
import { useTranslation } from "react-i18next";

import useAlert from '../../Alerts/useAlert';
import CentredSpinner from '../../Components/CentredSpinner';
import { UserPreferencesContext } from '../../Contexts/UserPreferencesContext';
import DateRange from '../../DateRange';
import { BusinessUnit } from '../../Types/BusinessUnit';
import { useBusinessUnits } from '../../Utilities/useBusinessUnits';
import { useGrades } from '../../Utilities/useGrades';
import { useHttpUtils } from '../../Utilities/useHttpUtils';

interface CsvTableProps {
    data: string | undefined,
}

function CsvTable({ data }: CsvTableProps) {

    const splitCsvData = (csvData: string) => {
        // "magic" regex to split on commas that are not within quotes, allowing for commas within quoted values
        let values = csvData.split(/,(?=(?:(?:[^"]*"){2})*[^"]*$)/);

        let result = values.map(item => item.trim().replace(/^"|"$/g, ''));

        return result;
    }

    return (
        data ?
        <BTable responsive striped bordered hover size="sm">
            {data.split("\n").map(row =>
                <tr>
                    {splitCsvData(row).map(cell =>
                        <td>{cell}</td>
                    )}
                </tr>
            )}
            </BTable>
        : <></>
    );
}

export default function GradedQualityReport() {
    const [loadingReport, setLoadingReport] = useState<boolean>(false);
    const [report, setReport] = useState<string>();
    const { fetchBlobAuthenticated } = useHttpUtils();
    const { setAlert } = useAlert();
    const { t } = useTranslation();

    const getFromDate = () => {
        const yesterdayStart = DateTime.now().minus({ day: 1 }).startOf('day');
        // Using toISO creates a string which includes the correct offset so any JS Date created from this string also mantains that offset
        // This is in constrast to typically making JSDates which uses the browsers time zone to create and assign offsets
        return yesterdayStart.toISO();
    };

    const getToDate = () => {
        const yesterdayStart = DateTime.now().minus({ day: 1 }).startOf('day');
        return yesterdayStart.toISO();
    };

    const defaultDateRange = {
        from: getFromDate(),
        to: getToDate()
    };

    const { businessUnits } = useBusinessUnits();
    const { userPreferences } = use(UserPreferencesContext);
    const [selectedBusinessUnit, setSelectedBusinessUnit] = useState<BusinessUnit>();
    const [selectedGrade, setSelectedGrade] = useState<string>();
    const [dateFilter, setDateFilter] = useState(defaultDateRange);
    const [formValidated, setFormValidated] = useState(false);
    const { grades, loading: loadingGrades, getGrades } = useGrades(selectedBusinessUnit?.code);

    useEffect(() => {

        if (selectedBusinessUnit?.code !== undefined) {
            getGrades(selectedBusinessUnit?.code);
        }

    }, [selectedBusinessUnit?.code]);

    useEffect(() => {
        setSelectedBusinessUnit(businessUnits.find(bu => userPreferences?.lotList?.defaultBusinessUnits?.find(code => code === bu.code)));
    }, [businessUnits, userPreferences?.lotList?.defaultBusinessUnits]);

    const getGradedQualityReport = async (date: DateTime | undefined = undefined) => {
        setLoadingReport(true);

        let from: DateTime;
        let to: DateTime;

        if (date !== undefined) {
            from = date;
            to = date;
        } else {
            from = DateTime.fromISO(dateFilter?.from);
            to = DateTime.fromISO(dateFilter?.to);
        }

        let url = `api/v1/lots/gradedqualityreport?dateFromUTC=${from.toUTC().toISO()}&dateToUTC=${to.toUTC().toISO()}`;
        if (selectedBusinessUnit) {
            url = url + `&businessUnit=${selectedBusinessUnit.code}&grade=${selectedGrade}`;
        }

        if (selectedGrade) {
            url = url + `&grade=${selectedGrade}`;
        }

        await fetchBlobAuthenticated(url)
            .then(async response => {
                if (response.ok) {
                    return response.text();
                } else {
                    throw new Error(await response.text());
                }
            })
            .then(text => {
                setLoadingReport(false);

                setReport(text);
            })
            .catch(error => {
                setLoadingReport(false);
                setAlert("danger", "Error", error.message);
            });
    };

    const downloadReport = () => {
        const element = document.createElement('a');
        const file = new Blob([report as BlobPart], { type: 'text/csv; charset=utf-8' });
        const url = URL.createObjectURL(file);
        element.href = url;
        element.download = `graded-quality-report.csv`;
        element.click();
    };

    const handleSubmit = (event: any) => {
        event.preventDefault();
        event.stopPropagation();

        setFormValidated(true);

        const form = event.currentTarget;

        if (form.checkValidity() === true) {
            getGradedQualityReport();
        }
    };

    return (
        <>
            <Form id="gradedQualityReportForm" noValidate validated={formValidated} onSubmit={handleSubmit}>
                <div className="d-flex mb-3">
                    <div className="d-inline-flex me-3">
                        <DateRange dateFilter={dateFilter} setDateFilter={setDateFilter} />
                    </div>
                    <div className="d-inline-flex me-3">
                        <Form.Select
                            required
                            value={selectedBusinessUnit?.code}
                            onChange={(e) => setSelectedBusinessUnit(businessUnits.find(x => x.code === e.target.value))}>
                            <option value="">{t("Application.Controls.DefaultSelect")}</option>
                            {businessUnits &&
                                businessUnits.map((bu, i) => {
                                    return <option key={`bu-option-${i}`} value={bu.code}>{bu.code}</option>
                                })
                            }
                        </Form.Select>
                    </div>
                    <div>
                        <Form.Select
                            required
                            disabled={!selectedBusinessUnit}
                            value={selectedGrade}
                            onChange={(e) => setSelectedGrade(e.target.value)}>
                            <option value="">{t("Application.Controls.DefaultSelect")}</option>
                            <option value="Any">{t("Application.Controls.SelectAny")}</option>
                            {grades &&
                                grades.map((grade, i) => {
                                    return <option key={`grade-option-${i}`} value={grade}>{grade}</option>
                                })
                            }
                        </Form.Select>
                    </div>
                    <div className="ms-auto ms-3">
                        <Button variant="primary" type="submit" form="gradedQualityReportForm" disabled={!selectedGrade || loadingReport}>
                            <>
                                {t("GradedQualityReport.DisplayData")}
                                {loadingReport &&
                                    <Spinner
                                        as="span"
                                        animation="border"
                                        size="sm"
                                        role="status"
                                        aria-hidden="true"
                                        className="ms-2"
                                    />
                                }
                            </>
                        </Button>
                    </div>
                </div>
                {loadingReport
                    ? <CentredSpinner />
                    :
                    report?
                        <>
                            <div className="ms-auto" >
                                <Button onClick={downloadReport} className="me-1"><FontAwesomeIcon icon={faDownload} className="me-1" />{t("Application.Controls.Download")}</Button>
                            </div>
                            <CsvTable data={report}></CsvTable>
                        </>
                        :
                        <h4 className="d-flex justify-content-center mt-5">
                            {t("GradedQualityReport.NoRecords")}
                        </h4>
                }
            </Form>
        </>
    );
}